import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiMessageCircle,
  FiPlus,
  FiThumbsUp,
  FiCheckCircle,
  FiMessageSquare,
  FiX,
  FiLock,
  FiCornerDownRight,
  FiSend,
} from "react-icons/fi";
import { workshopApi } from "../../services/workshopApi";
import LoadingSpinner from "../ui/LoadingSpinner";
import "./DiscussionForum.css";

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.2, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.15 },
  },
};

// Format time ago
const formatTimeAgo = (date) => {
  const now = new Date();
  const discussionDate = new Date(date);
  const diffInMs = now - discussionDate;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInHours < 24) return `${diffInHours}h ago`;
  return `${diffInDays}d ago`;
};

// Reply Card Component
const ReplyCard = ({ reply, workshopId, discussionId }) => {
  const [upvoteCount, setUpvoteCount] = useState(reply.upvoteCount || 0);
  const [userHasUpvoted, setUserHasUpvoted] = useState(reply.userHasUpvoted || false);

  const handleUpvoteReply = async () => {
    try {
      const response = await workshopApi.upvoteReply(workshopId, discussionId, reply._id);
      // Update state based on API response (toggle logic)
      setUpvoteCount(response.data?.upvoteCount ?? upvoteCount + (response.data?.action === 'added' ? 1 : -1));
      setUserHasUpvoted(response.data?.action === 'added');
    } catch (error) { /* ignored */ }
  };

  return (
    <motion.div
      className="wdf-reply-card"
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
    >
      <img
        src={reply.author.profilePhotoUrl || "/default-avatar.png"}
        alt={reply.author.name}
        className="wdf-reply-avatar"
        onError={(e) => {
          e.target.style.display = "none";
          e.target.nextSibling.style.display = "flex";
        }}
      />
      <div className="wdf-reply-avatar-fallback">
        {reply.author.name?.charAt(0).toUpperCase()}
      </div>
      <div className="wdf-reply-content">
        <div className="wdf-reply-header">
          <div className="wdf-reply-info">
            <span className="wdf-reply-name">{reply.author.name}</span>
            <span className="wdf-reply-time">
              {formatTimeAgo(reply.createdAt)}
            </span>
          </div>
          <button 
            className={`wdf-reply-upvote ${userHasUpvoted ? 'wdf-upvoted' : ''}`} 
            onClick={handleUpvoteReply}
            title={userHasUpvoted ? 'Remove upvote' : 'Upvote'}
          >
            <FiThumbsUp size={12} />
            {upvoteCount}
          </button>
        </div>
        <p className="wdf-reply-text">{reply.content}</p>
      </div>
    </motion.div>
  );
};

// Reply Form Component
const ReplyForm = ({ onSubmit, onCancel }) => {
  const [content, setContent] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!content.trim()) {
      alert("Please enter a reply");
      return;
    }
    onSubmit({ content });
    setContent("");
  };

  return (
    <motion.div
      className="wdf-reply-form"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
    >
      <form onSubmit={handleSubmit}>
        <textarea
          className="wdf-reply-textarea"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Write your reply..."
          rows={3}
          maxLength={2000}
          required
        />
        <div className="wdf-reply-actions">
          <button type="submit" className="wdf-btn wdf-btn-primary">
            <FiSend />
            Post Reply
          </button>
          <button
            type="button"
            className="wdf-btn wdf-btn-secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </form>
    </motion.div>
  );
};

// Discussion Card Component
const DiscussionCard = ({
  discussion,
  workshopId,
  onUpvote,
  onMarkResolved,
  userRole,
  onReplyUpdate,
}) => {
  const [showReplies, setShowReplies] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replies, setReplies] = useState(discussion.replies || []);
  const [showFullContent, setShowFullContent] = useState(false);

  const handleReply = async (replyData) => {
    try {
      const response = await workshopApi.replyToDiscussion(
        workshopId,
        discussion._id,
        replyData
      );

      setShowReplyForm(false);

      if (response.success && response.data) {
        const newReply = response.data.reply || replyData;
        setReplies((prevReplies) => [...prevReplies, newReply]);
        onReplyUpdate(discussion._id, newReply);
      }
    } catch (error) {
alert("Failed to post reply. Please try again.");
    }
  };

  const canResolve = userRole === "organizer" && discussion.type === "question";

  const getBadgeClass = (type) => {
    const badges = {
      question: "wdf-badge-question",
      discussion: "wdf-badge-discussion",
      announcement: "wdf-badge-announcement",
    };
    return badges[type] || "wdf-badge-discussion";
  };

  return (
    <motion.div className="wdf-card" variants={itemVariants}>
      <div className="wdf-card-header">
        <div className="wdf-card-badges">
          <span className={`wdf-badge ${getBadgeClass(discussion.type)}`}>
            {discussion.type}
          </span>
          {/* Show day label - use sessionDayLabel from backend, or generate from sessionDay */}
          {(() => {
            // Use backend-provided label, or generate from sessionDay number
            const dayLabel = discussion.sessionDayLabel || 
              (discussion.sessionDay === 0 ? 'General' : 
               discussion.sessionDay ? `Day ${discussion.sessionDay}` : null);
            
            if (!dayLabel) return null;
            
            return (
              <span className={`wdf-badge wdf-badge-day ${dayLabel === 'General' ? 'wdf-badge-general' : ''}`}>
                {dayLabel}
              </span>
            );
          })()}
          {discussion.isPinned && (
            <span className="wdf-badge wdf-badge-pinned">📌 Pinned</span>
          )}
          {discussion.isResolved && (
            <span className="wdf-badge wdf-badge-resolved">
              <FiCheckCircle size={12} /> Resolved
            </span>
          )}
        </div>
        <div className="wdf-card-actions">
          {/* Upvote button - shows filled/unfilled based on userHasUpvoted */}
          <button 
            className={`wdf-upvote-btn ${discussion.userHasUpvoted ? 'wdf-upvoted' : ''}`} 
            onClick={onUpvote}
            title={discussion.userHasUpvoted ? 'Remove upvote' : 'Upvote'}
          >
            <FiThumbsUp />
            {discussion.upvoteCount || 0}
          </button>
          {canResolve && !discussion.isResolved && (
            <button className="wdf-resolve-btn" onClick={onMarkResolved}>
              <FiCheckCircle />
              Resolve
            </button>
          )}
        </div>
      </div>

      <h3 className="wdf-card-title">{discussion.title}</h3>

      <div className="wdf-card-content">
        {discussion.content &&
        discussion.content.length > 200 &&
        !showFullContent
          ? discussion.content.substring(0, 200) + "..."
          : discussion.content}
        {discussion.content && discussion.content.length > 200 && (
          <button
            className="wdf-see-more-btn"
            onClick={() => setShowFullContent(!showFullContent)}
          >
            {showFullContent ? "See less" : "See more"}
          </button>
        )}
      </div>

      {discussion.tags?.length > 0 && (
        <div className="wdf-tags">
          {discussion.tags.map((tag, index) => (
            <span key={index} className="wdf-tag">
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="wdf-card-footer">
        <div className="wdf-author">
          <img
            src={discussion.author.profilePhotoUrl || "/default-avatar.png"}
            alt={discussion.author.name}
            className="wdf-author-avatar"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <div className="wdf-author-fallback">
            {discussion.author.name?.charAt(0).toUpperCase()}
          </div>
          <div className="wdf-author-info">
            <span className="wdf-author-name">{discussion.author.name}</span>
            <span className="wdf-separator">•</span>
            <span className="wdf-time">
              {formatTimeAgo(discussion.createdAt)}
            </span>
          </div>
        </div>

        <div className="wdf-stats">
          <button
            className="wdf-stat-btn"
            onClick={() => setShowReplies(!showReplies)}
          >
            <FiMessageSquare />
            {discussion.replyCount || replies.length || 0}
          </button>
          <button
            className="wdf-stat-btn"
            onClick={() => setShowReplyForm(!showReplyForm)}
          >
            <FiCornerDownRight />
            Reply
          </button>
        </div>
      </div>

      {/* Replies Section */}
      <AnimatePresence>
        {showReplies && replies.length > 0 && (
          <motion.div
            className="wdf-replies"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            {replies.map((reply, index) => (
              <ReplyCard
                key={index}
                reply={reply}
                workshopId={workshopId}
                discussionId={discussion._id}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reply Form */}
      <AnimatePresence>
        {showReplyForm && (
          <ReplyForm
            onSubmit={handleReply}
            onCancel={() => setShowReplyForm(false)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Create Discussion Form Component
const DiscussionCreateForm = ({ onSubmit, onCancel, workshopDays = 3 }) => {
  const [formData, setFormData] = useState({
    type: "discussion",
    title: "",
    content: "",
    sessionDay: 0, // Default: 0 = General discussion (not day-specific)
    tags: [],
  });
  const [tagInput, setTagInput] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      alert("Please fill in both title and content");
      return;
    }
    onSubmit(formData);
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()],
      });
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  return (
    <motion.div
      className="wdf-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onCancel}
    >
      <motion.div
        className="wdf-modal"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="wdf-modal-header">
          <h3 className="wdf-modal-title">Start New Discussion</h3>
          <button className="wdf-modal-close" onClick={onCancel}>
            <FiX />
          </button>
        </div>

        <div className="wdf-modal-body">
          <form onSubmit={handleSubmit}>
            <div className="wdf-form-group">
              <label className="wdf-form-label">Type</label>
              <select
                className="wdf-form-select"
                value={formData.type}
                onChange={(e) =>
                  setFormData({ ...formData, type: e.target.value })
                }
                required
              >
                <option value="discussion">General Discussion</option>
                <option value="question">Question</option>
              </select>
            </div>

            <div className="wdf-form-group">
              <label className="wdf-form-label">
                Title <span className="required">*</span>
              </label>
              <input
                type="text"
                className="wdf-form-input"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="Enter discussion title..."
                maxLength={200}
                required
              />
            </div>

            <div className="wdf-form-group">
              <label className="wdf-form-label">
                Content <span className="required">*</span>
              </label>
              <textarea
                className="wdf-form-textarea"
                value={formData.content}
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
                placeholder="What would you like to discuss?"
                rows={4}
                maxLength={5000}
                required
              />
            </div>

            <div className="wdf-form-group">
              <label className="wdf-form-label">Session Day (Optional)</label>
              <select
                className="wdf-form-select"
                value={formData.sessionDay}
                onChange={(e) =>
                  setFormData({ ...formData, sessionDay: parseInt(e.target.value) || 0 })
                }
              >
                {/* sessionDay: 0 = General (not day-specific), 1+ = specific day */}
                <option value={0}>General (Not day-specific)</option>
                {/* Generate day options dynamically from workshopDays */}
                {Array.from({ length: workshopDays }, (_, i) => (
                  <option key={i + 1} value={i + 1}>Day {i + 1}</option>
                ))}
              </select>
            </div>

            <div className="wdf-form-group">
              <label className="wdf-form-label">Tags</label>
              <div className="wdf-tag-input-wrapper">
                <input
                  type="text"
                  className="wdf-tag-input"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  placeholder="Add a tag"
                />
                <button
                  type="button"
                  className="wdf-add-tag-btn"
                  onClick={addTag}
                >
                  <FiPlus />
                </button>
              </div>
              {formData.tags.length > 0 && (
                <div className="wdf-tags-list">
                  {formData.tags.map((tag, index) => (
                    <span key={index} className="wdf-tag-item">
                      {tag}
                      <button
                        type="button"
                        className="wdf-tag-remove"
                        onClick={() => removeTag(tag)}
                      >
                        <FiX />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="wdf-form-actions">
              <button type="submit" className="wdf-btn wdf-btn-primary">
                <FiMessageCircle />
                Post Discussion
              </button>
              <button
                type="button"
                className="wdf-btn wdf-btn-secondary"
                onClick={onCancel}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Component
const DiscussionForum = ({ workshopId, userRole, workshop, onNavigateToOverview }) => {
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({
    type: "all",
    sessionDay: "",
    search: "",
    sortBy: "recent",
  });
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Get workshop days dynamically from workshop.duration.days
  const workshopDays = workshop?.duration?.days || 3;

  // Debounce timer ref for filter/search changes
  const debounceTimerRef = useRef(null);

  // Debounced effect for filter changes - waits 300ms after user stops changing filters
  useEffect(() => {
    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set a new debounce timer (300ms delay)
    debounceTimerRef.current = setTimeout(() => {
      fetchDiscussions();
    }, 300);

    // Cleanup function to clear timer on unmount or filter change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workshopId, filter]);

  const fetchDiscussions = async () => {
    try {
      setLoading(true);
      const response = await workshopApi.getWorkshopDiscussions(
        workshopId,
        filter
      );
      if (response.success) {
        setDiscussions(response.data.discussions || []);
      }
    } catch (error) {
      setError(error.message);
} finally {
      setLoading(false);
    }
  };

  const handleCreateDiscussion = async (discussionData) => {
    try {
      await workshopApi.createDiscussion(workshopId, discussionData);
      setShowCreateForm(false);
      fetchDiscussions();
    } catch (error) {
alert("Failed to create discussion. Please try again.");
    }
  };

  const handleUpvote = useCallback(
    async (discussionId) => {
      try {
        const response = await workshopApi.upvoteDiscussion(workshopId, discussionId);

        // Update local state based on API response
        // Backend returns { action: "added" | "removed", upvoteCount: number }
        setDiscussions((prevDiscussions) =>
          prevDiscussions.map((discussion) =>
            discussion._id === discussionId
              ? {
                  ...discussion,
                  upvoteCount: response.data?.upvoteCount ?? (discussion.upvoteCount || 0) + (response.data?.action === 'added' ? 1 : -1),
                  userHasUpvoted: response.data?.action === 'added'
                }
              : discussion
          )
        );
      } catch (error) {
alert("Failed to upvote discussion. Please try again.");
      }
    },
    [workshopId]
  );

  const handleMarkResolved = useCallback(
    async (discussionId) => {
      try {
        await workshopApi.markQuestionAsResolved(workshopId, discussionId);

        setDiscussions((prevDiscussions) =>
          prevDiscussions.map((discussion) =>
            discussion._id === discussionId
              ? { ...discussion, isResolved: true }
              : discussion
          )
        );
      } catch (error) {
alert("Failed to mark question as resolved. Please try again.");
      }
    },
    [workshopId]
  );

  const handleReplyUpdate = useCallback((discussionId, newReply) => {
    setDiscussions((prevDiscussions) =>
      prevDiscussions.map((discussion) =>
        discussion._id === discussionId
          ? {
              ...discussion,
              replyCount: (discussion.replyCount || 0) + 1,
              replies: [...(discussion.replies || []), newReply],
            }
          : discussion
      )
    );
  }, []);

  // Loading State
  if (loading) {
    return (
      <div className="wdf-loading-skeleton">
        {[1, 2, 3].map((i) => (
          <div key={i} className="wdf-skeleton-card">
            <div className="wdf-skeleton-header">
              <div className="wdf-skeleton-avatar" />
              <div className="wdf-skeleton-meta">
                <div className="wdf-skeleton-line wdf-skeleton-name" />
                <div className="wdf-skeleton-line wdf-skeleton-time" />
              </div>
            </div>
            <div className="wdf-skeleton-content">
              <div className="wdf-skeleton-line wdf-skeleton-title" />
              <div className="wdf-skeleton-line wdf-skeleton-text" />
              <div className="wdf-skeleton-line wdf-skeleton-text-short" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Error State - Access Denied
  if (error) {
    const isAccessDenied =
      error.includes("403") ||
      error.includes("401") ||
      error.includes("503") ||
      error.includes("unauthorized") ||
      error.includes("not a participant") ||
      error.includes("API call failed with status 403");

    if (isAccessDenied || userRole === "non-member") {
      return (
        <motion.div
          className="wdf-access-denied"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="wdf-access-denied-icon">
            <FiLock />
          </div>
          <h3 className="wdf-access-denied-title">
            Discussion Access Restricted
          </h3>
          <p className="wdf-access-denied-text">
            You need to be a workshop participant to view and participate in
            discussions.
          </p>
          <button
            className="wdf-btn wdf-btn-primary"
            onClick={onNavigateToOverview}
          >
            View Workshop Details
          </button>
        </motion.div>
      );
    }

    return (
      <motion.div
        className="wdf-error"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <p className="wdf-error-text">Failed to load discussions: {error}</p>
        <button className="wdf-retry-btn" onClick={fetchDiscussions}>
          Try Again
        </button>
      </motion.div>
    );
  }

  return (
    <div className="wdf-container">
      {/* Header */}
      <motion.div
        className="wdf-header"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="wdf-header-left">
          <div className="wdf-header-icon">
            <FiMessageCircle />
          </div>
          <div>
            <h2 className="wdf-header-title">Workshop Discussions</h2>
            <span className="wdf-header-count">
              {discussions.length} discussions
            </span>
          </div>
        </div>
        <button
          className="wdf-create-btn"
          onClick={() => setShowCreateForm(true)}
        >
          <FiPlus />
          Start Discussion
        </button>
      </motion.div>

      {/* Filters */}
      <motion.div
        className="wdf-filters"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="wdf-filter-group">
          <select
            className="wdf-filter-select"
            value={filter.type}
            onChange={(e) => setFilter({ ...filter, type: e.target.value })}
          >
            <option value="all">All Types</option>
            <option value="question">Questions</option>
            <option value="discussion">Discussions</option>
            <option value="announcement">Announcements</option>
          </select>

          <select
            className="wdf-filter-select"
            value={filter.sessionDay}
            onChange={(e) =>
              setFilter({ ...filter, sessionDay: e.target.value })
            }
          >
            <option value="">All Days</option>
            {/* sessionDay: 0 = General, 1+ = specific day */}
            <option value="0">General</option>
            {/* Generate day options dynamically from workshop.duration.days */}
            {Array.from({ length: workshopDays }, (_, i) => (
              <option key={i + 1} value={i + 1}>Day {i + 1}</option>
            ))}
          </select>

          <select
            className="wdf-filter-select"
            value={filter.sortBy}
            onChange={(e) => setFilter({ ...filter, sortBy: e.target.value })}
          >
            <option value="recent">Most Recent</option>
            <option value="popular">Most Popular</option>
            <option value="unanswered">Unanswered</option>
          </select>
        </div>

        <div className="wdf-search-wrapper">
          <input
            type="text"
            className="wdf-search-input"
            placeholder="Search discussions..."
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
          />
        </div>
      </motion.div>

      {/* Discussions List */}
      <motion.div
        className="wdf-list"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {discussions.length === 0 ? (
          <motion.div className="wdf-empty" variants={itemVariants}>
            <div className="wdf-empty-icon">
              <FiMessageCircle />
            </div>
            <h3 className="wdf-empty-title">No discussions yet</h3>
            <p className="wdf-empty-text">
              Be the first to start a discussion!
            </p>
          </motion.div>
        ) : (
          discussions.map((discussion) => (
            <DiscussionCard
              key={discussion._id}
              discussion={discussion}
              workshopId={workshopId}
              onUpvote={() => handleUpvote(discussion._id)}
              onMarkResolved={() => handleMarkResolved(discussion._id)}
              userRole={userRole}
              onReplyUpdate={handleReplyUpdate}
            />
          ))
        )}
      </motion.div>

      {/* Create Discussion Modal */}
      <AnimatePresence>
        {showCreateForm && (
          <DiscussionCreateForm
            onSubmit={handleCreateDiscussion}
            onCancel={() => setShowCreateForm(false)}
            workshopDays={workshopDays}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default DiscussionForum;
